import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  InputAdornment,
  Alert,
  Fade,
  Slide,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { Email, LockReset } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import axios from 'axios';

export default function ForgetPassword() {
  const theme = useTheme();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [counter, setCounter] = useState(5);

  useEffect(() => {
    counter > 0 && successMessage && setTimeout(() => setCounter(counter - 1), 1000);
  }, [counter, successMessage]);

  const resetPassWordURL = `${process.env.REACT_APP_SERVER_API}/users/forgotPassword`;

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({});

  const onSubmit = async () => {
    setIsLoading(true);
    const email = getValues('email');

    try {
      await axios.put(resetPassWordURL, {
        email: email,
      });
      setSuccessMessage('The reset link has been sent to your email!');
      setTimeout(() => {
        history.push('/signin');
      }, 5000);
    } catch (err) {
      setIsLoading(false);
      // Error will be shown via form validation
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 50%, #334155 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 50%, ${theme.palette.secondary.main} 100%)`,
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          transform: 'translate(-50%, -50%)',
          background:
            theme.palette.mode === 'dark'
              ? `radial-gradient(circle, ${theme.alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`
              : `radial-gradient(circle, ${theme.alpha('#FFFFFF', 0.2)} 0%, transparent 70%)`,
          animation: 'pulse 15s ease-in-out infinite',
        },
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 1,
            transform: 'translate(-50%, -50%) scale(1)',
          },
          '50%': {
            opacity: 0.8,
            transform: 'translate(-50%, -50%) scale(1.1)',
          },
        },
      }}
    >
      <Container
        component="main"
        maxWidth="sm"
        sx={{
          transform: 'scale(0.8)',
          transformOrigin: 'center center',
        }}
      >
        <Slide direction="down" in={true} timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              padding: { xs: 4, sm: 6 },
              background:
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 41, 59, 0.8)'
                  : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.3)'
              }`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
                  : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 12px 48px 0 rgba(0, 0, 0, 0.5)'
                    : '0 12px 48px 0 rgba(31, 38, 135, 0.5)',
              },
            }}
          >
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Fade in={true} timeout={1000}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    mb: 3,
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  <LockReset sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Fade>

              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: '1.1rem' }}
              >
                Enter your email to receive a reset link
              </Typography>
            </Box>

            {/* Success Alert */}
            {successMessage && (
              <Fade in={true}>
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                  }}
                >
                  {successMessage}
                  <br />
                  Redirecting to sign in in {counter} seconds...
                </Alert>
              </Fade>
            )}

            {/* Reset Form */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                disabled={isLoading || !!successMessage}
                error={!!errors.email}
                helperText={errors.email?.message}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    },
                    '&.Mui-focused': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${theme.alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address',
                  },
                })}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !!successMessage}
                sx={{
                  py: 1.8,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: `0 4px 16px ${theme.alpha(theme.palette.primary.main, 0.4)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${theme.alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                  },
                  '&.Mui-disabled': {
                    background: theme.palette.action.disabledBackground,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={28} sx={{ color: 'white' }} />
                ) : (
                  'Send Reset Link'
                )}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <MuiLink
                  href="/signin"
                  variant="body2"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'all 0.3s',
                    '&:hover': {
                      textDecoration: 'underline',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  Back to Sign In
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Container>
    </Box>
  );
}
